/**
 * BNB 底池各工厂 vaultData 编码（链上 vaultDataSchema 确认）
 */
import { encodeAbiParameters, parseUnits, zeroAddress } from "viem";
import { isAddr, ZERO } from "./bbrokerVaultData.js";

export { isAddr, ZERO };

/** Portal 顶层 dividendToken 哨兵（hold / dual 等工厂 policy：重复 c0De） */
export const MAGIC_DIVIDEND_VAULT =
  "0xC0Dec0dec0DeC0Dec0dEc0DEC0DEC0DEC0DEC0dE";

export const BNB_QUOTE = zeroAddress;

function encTuple(components, value) {
  try {
    const hex = encodeAbiParameters([{ type: "tuple", components }], [value]);
    return { hex };
  } catch (e) {
    return { hex: "0x", error: e instanceof Error ? e.message : "编码失败" };
  }
}

function parseBnbWei(human, label = "BNB 金额") {
  try {
    const wei = parseUnits(String(human ?? "0").trim() || "0", 18);
    if (wei < 0n) return { error: `${label}不能为负` };
    return { wei };
  } catch {
    return { error: `${label}格式无效` };
  }
}

function requireAddr(a, label) {
  const v = String(a || "").trim();
  if (!isAddr(v) || v.toLowerCase() === ZERO.toLowerCase()) {
    return { error: `请填写有效的${label}` };
  }
  return { addr: v };
}

/** 空参工厂 */
export function encodeEmptyVaultData() {
  return { hex: "0x", emptyOk: true };
}

export const EMPTY_DEFAULTS = {};

/** half-perpetual: marketingWallet */
export const HALF_PERPETUAL_DEFAULTS = { marketingWallet: "" };
export function encodeHalfPerpetualVaultData(v) {
  const w = requireAddr(v.marketingWallet, "营销钱包");
  if (w.error) return { hex: "0x", error: w.error };
  return encTuple([{ type: "address", name: "marketingWallet" }], {
    marketingWallet: w.addr,
  });
}

/** floor-protocol / viral-reserve / alpha-points: communityWallet */
export const COMMUNITY_DEFAULTS = { communityWallet: "" };
export function encodeCommunityWalletVaultData(v) {
  const w = requireAddr(v.communityWallet, "社区钱包");
  if (w.error) return { hex: "0x", error: w.error };
  return encTuple([{ type: "address", name: "communityWallet" }], {
    communityWallet: w.addr,
  });
}

/** alpha-points：链上 vaultData = address opsWallet（32 字节） */
export const OPS_WALLET_DEFAULTS = { opsWallet: "" };
export function encodeOpsWalletVaultData(v) {
  const w = requireAddr(v.opsWallet, "运营钱包");
  if (w.error) return { hex: "0x", error: w.error };
  return encTuple([{ type: "address", name: "opsWallet" }], {
    opsWallet: w.addr,
  });
}

/** niu-floor */
export const NIU_FLOOR_DEFAULTS = {
  communityWallet: "",
  buybackIntervalMinutes: "10",
  buybackRatioBps: "600",
};
export function encodeNiuFloorVaultData(v) {
  const w = requireAddr(v.communityWallet, "社区钱包");
  if (w.error) return { hex: "0x", error: w.error };
  const mins = Math.round(Number(v.buybackIntervalMinutes));
  const ratio = Math.round(Number(v.buybackRatioBps));
  if (!Number.isFinite(mins) || mins < 0) {
    return { hex: "0x", error: "回购间隔（分钟）无效" };
  }
  if (!Number.isFinite(ratio) || ratio < 0 || ratio > 10000) {
    return { hex: "0x", error: "回购占比 bps 须在 0–10000" };
  }
  return encTuple(
    [
      { type: "address", name: "communityWallet" },
      { type: "uint256", name: "buybackIntervalMinutes" },
      { type: "uint16", name: "buybackRatioBps" },
    ],
    {
      communityWallet: w.addr,
      buybackIntervalMinutes: BigInt(mins),
      buybackRatioBps: ratio,
    }
  );
}

/** dual-buyback ddf-v2 */
export const DUAL_BUYBACK_DEFAULTS = {
  intervalSeconds: "60",
  amountPerBuybackHuman: "0.01",
  otherToken: "",
};
export function encodeDualBuybackVaultData(v) {
  const interval = Math.round(Number(v.intervalSeconds));
  if (!Number.isFinite(interval) || interval < 0) {
    return { hex: "0x", error: "回购间隔（秒）无效" };
  }
  const amt = parseBnbWei(v.amountPerBuybackHuman, "每次回购 BNB");
  if (amt.error) return { hex: "0x", error: amt.error };
  const o = requireAddr(v.otherToken, "外币地址");
  if (o.error) return { hex: "0x", error: o.error };
  return encTuple(
    [
      { type: "uint256", name: "intervalSeconds" },
      { type: "uint256", name: "amountPerBuyback" },
      { type: "address", name: "otherToken" },
    ],
    {
      intervalSeconds: BigInt(interval),
      amountPerBuyback: amt.wei,
      otherToken: o.addr,
    }
  );
}

/** dual-buyback-tax-adjustable daf-v1 */
export const DUAL_BUYBACK_TAX_DEFAULTS = {
  ...DUAL_BUYBACK_DEFAULTS,
  /** 可见默认 ≈ 主币 79% / 外币 20% / 裂变 1%（合计 10000） */
  selfBurnBps: "7900",
  otherBurnBps: "2000",
  fissionBps: "100",
};
export function encodeDualBuybackTaxVaultData(v) {
  const base = encodeDualBuybackVaultData(v);
  if (base.error) return base;
  const selfBurnBps = Math.round(Number(v.selfBurnBps ?? 0));
  const otherBurnBps = Math.round(Number(v.otherBurnBps ?? 0));
  const fissionBps = Math.round(Number(v.fissionBps ?? 0));
  for (const [n, x] of [
    ["主币占比", selfBurnBps],
    ["外币占比", otherBurnBps],
    ["裂变占比", fissionBps],
  ]) {
    if (!Number.isFinite(x) || x < 0 || x > 10000) {
      return { hex: "0x", error: `${n}须在 0–10000` };
    }
  }
  const sum = selfBurnBps + otherBurnBps + fissionBps;
  if (sum !== 0 && sum !== 10000) {
    return { hex: "0x", error: "三个占比须全 0（用默认）或合计 10000" };
  }
  const interval = Math.round(Number(v.intervalSeconds));
  const amt = parseBnbWei(v.amountPerBuybackHuman, "每次回购 BNB");
  const o = requireAddr(v.otherToken, "外币地址");
  return encTuple(
    [
      { type: "uint256", name: "intervalSeconds" },
      { type: "uint256", name: "amountPerBuyback" },
      { type: "address", name: "otherToken" },
      { type: "uint16", name: "selfBurnBps" },
      { type: "uint16", name: "otherBurnBps" },
      { type: "uint16", name: "fissionBps" },
    ],
    {
      intervalSeconds: BigInt(interval),
      amountPerBuyback: amt.wei,
      otherToken: o.addr,
      selfBurnBps,
      otherBurnBps,
      fissionBps,
    }
  );
}

/** minute-buyback bbf-v4 */
export const MINUTE_BUYBACK_DEFAULTS = {
  intervalSeconds: "60",
  amountPerBuybackHuman: "0.01",
};
export function encodeMinuteBuybackVaultData(v) {
  const interval = Math.round(Number(v.intervalSeconds));
  if (!Number.isFinite(interval) || interval < 0) {
    return { hex: "0x", error: "回购间隔（秒）无效" };
  }
  const amt = parseBnbWei(v.amountPerBuybackHuman, "每次回购 BNB");
  if (amt.error) return { hex: "0x", error: amt.error };
  return encTuple(
    [
      { type: "uint256", name: "intervalSeconds" },
      { type: "uint256", name: "amountPerBuyback" },
    ],
    {
      intervalSeconds: BigInt(interval),
      amountPerBuyback: amt.wei,
    }
  );
}

/** percent-buyback pbb-v1 — 同 spbb 形态 */
export const PBB_DEFAULTS = {
  intervalSeconds: "60",
  spendBps: "1000",
};
export function encodePbbVaultData(v) {
  const interval = Math.round(Number(v.intervalSeconds));
  const spend = Math.round(Number(v.spendBps));
  if (!Number.isFinite(interval) || interval < 0) {
    return { hex: "0x", error: "回购间隔无效（秒）" };
  }
  if (!Number.isFinite(spend) || spend < 100 || spend > 10000) {
    return { hex: "0x", error: "每次消耗须在 100–10000 bps" };
  }
  return encTuple(
    [
      { type: "uint256", name: "intervalSeconds" },
      { type: "uint256", name: "spendBps" },
    ],
    {
      intervalSeconds: BigInt(interval),
      spendBps: BigInt(spend),
    }
  );
}

/** build 族：community + dividendToken */
export const BUILD_DIV_DEFAULTS = {
  communityWallet: "",
  dividendToken: "",
};
export function encodeBuildDivVaultData(v) {
  const c = requireAddr(v.communityWallet, "社区/基金会钱包");
  if (c.error) return { hex: "0x", error: c.error };
  const d = requireAddr(v.dividendToken, "分红代币");
  if (d.error) return { hex: "0x", error: d.error };
  return encTuple(
    [
      { type: "address", name: "communityWallet" },
      { type: "address", name: "dividendToken" },
    ],
    { communityWallet: c.addr, dividendToken: d.addr }
  );
}

/** pink-v1.5 lockDays */
export const PINK_LOCK_DEFAULTS = { lockDays: "365" };
export function encodePinkLockVaultData(v) {
  const days = Math.round(Number(v.lockDays));
  if (!Number.isFinite(days) || days < 7 || days > 1095) {
    return { hex: "0x", error: "锁仓天数须在 7–1095（默认 365）" };
  }
  return encTuple([{ type: "uint256", name: "lockDays" }], {
    lockDays: BigInt(days),
  });
}

/** hold-dividend-buyback */
export const HOLD_DIV_DEFAULTS = {
  dividendToken: "",
  /** 持币门槛（税币数量）；默认 1 万起步，与 Portal minimumShareBalance 同步 */
  minHoldAmountHuman: "10000",
  intervalSeconds: "60",
  amountPerBuybackHuman: "0.01",
};
export function encodeHoldDivVaultData(v) {
  const d = requireAddr(v.dividendToken, "分红币地址");
  if (d.error) return { hex: "0x", error: d.error };
  let minHold;
  try {
    minHold = parseUnits(String(v.minHoldAmountHuman ?? "0").trim() || "0", 18);
  } catch {
    return { hex: "0x", error: "持币门槛格式无效" };
  }
  if (minHold < 0n) {
    return { hex: "0x", error: "持币门槛不能为负" };
  }
  const interval = Math.round(Number(v.intervalSeconds));
  if (!Number.isFinite(interval) || interval < 0) {
    return { hex: "0x", error: "间隔秒数无效" };
  }
  const amt = parseBnbWei(v.amountPerBuybackHuman, "每次销毁 BNB");
  if (amt.error) return { hex: "0x", error: amt.error };
  return encTuple(
    [
      { type: "address", name: "dividendToken" },
      { type: "uint256", name: "minHoldAmount" },
      { type: "uint256", name: "intervalSeconds" },
      { type: "uint256", name: "amountPerBuyback" },
    ],
    {
      dividendToken: d.addr,
      minHoldAmount: minHold,
      intervalSeconds: BigInt(interval),
      amountPerBuyback: amt.wei,
    }
  );
}

/** hold-dividend-tax-adjustable */
export const HOLD_DIV_TAX_DEFAULTS = {
  ...HOLD_DIV_DEFAULTS,
  fissionBps: "0",
  burnBps: "0",
  dividendBps: "0",
};
export function encodeHoldDivTaxVaultData(v) {
  const base = encodeHoldDivVaultData(v);
  if (base.error) return base;
  const fissionBps = Math.round(Number(v.fissionBps ?? 0));
  const burnBps = Math.round(Number(v.burnBps ?? 0));
  const dividendBps = Math.round(Number(v.dividendBps ?? 0));
  for (const [n, x] of [
    ["裂变占比", fissionBps],
    ["销毁占比", burnBps],
    ["分红占比", dividendBps],
  ]) {
    if (!Number.isFinite(x) || x < 0 || x > 10000) {
      return { hex: "0x", error: `${n}须在 0–10000` };
    }
  }
  const sum = fissionBps + burnBps + dividendBps;
  if (sum !== 0 && sum !== 10000) {
    return { hex: "0x", error: "三个占比须全 0（用默认）或合计 10000" };
  }
  // 与 encodeHoldDivVaultData 相同门槛（原样写入，不改写 0）
  const d = requireAddr(v.dividendToken, "分红币地址");
  let minHold;
  try {
    minHold = parseUnits(String(v.minHoldAmountHuman ?? "0").trim() || "0", 18);
  } catch {
    return { hex: "0x", error: "持币门槛格式无效" };
  }
  const interval = Math.round(Number(v.intervalSeconds));
  const amt = parseBnbWei(v.amountPerBuybackHuman, "每次销毁 BNB");
  return encTuple(
    [
      { type: "address", name: "dividendToken" },
      { type: "uint256", name: "minHoldAmount" },
      { type: "uint256", name: "intervalSeconds" },
      { type: "uint256", name: "amountPerBuyback" },
      { type: "uint256", name: "fissionBps" },
      { type: "uint256", name: "burnBps" },
      { type: "uint256", name: "dividendBps" },
    ],
    {
      dividendToken: d.addr,
      minHoldAmount: minHold,
      intervalSeconds: BigInt(interval),
      amountPerBuyback: amt.wei,
      fissionBps: BigInt(fissionBps),
      burnBps: BigInt(burnBps),
      dividendBps: BigInt(dividendBps),
    }
  );
}
